<template>
  <ConfirmDialog ref="confirm"></ConfirmDialog>
  <Dialog
    v-model:visible="assessmentRoleDeniedVisibleModel"
    modal
    header="Cannot create assessment"
    :style="{ width: 'min(32rem, 92vw)' }"
    :closable="true"
  >
    <p class="m-0 text-gray-800 whitespace-pre-line">{{ assessmentRoleDeniedMessage }}</p>
    <template #footer>
      <Button
        label="OK"
        icon="pi pi-check"
        class="btn-primary-accent"
        unstyled
        @click="closeAssessmentRoleDenied"
        autofocus
      />
    </template>
  </Dialog>
  <Toast ref="toast" />
  <toast-notifications-component />
  <router-view />
  <overlay-container-component />
  <frontend-overlay-component />
  <home-screen-prompt />
</template>
<script>
import ToastNotificationsComponent from '@/components/global/ToastNotificationsComponent.vue';
import FrontendOverlayComponent from '@/components/global/FrontendOverlayComponent.vue';
import OverlayContainerComponent from '@/components/global/OverlayContainerComponent.vue';
import HomeScreenPrompt from '@/components/PWA/HomeScreenPrompt.vue';
import Toast from 'primevue/toast';
import ConfirmDialog from 'primevue/confirmdialog';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { mapGetters } from 'vuex';
import { applyPrimeVuePaginatorAria } from '@/includes/primevuePaginatorLocale';

export default {
  components: {
    OverlayContainerComponent,
    ToastNotificationsComponent,
    FrontendOverlayComponent,
    HomeScreenPrompt,
    Toast,
    ConfirmDialog,
    Dialog,
    Button,
  },
  computed: {
    ...mapGetters('notifications', ['toasts']),
    assessmentRoleDeniedMessage() {
      return this.$store.state.assessmentRoleDenied.dialogMessage;
    },
    assessmentRoleDeniedVisibleModel: {
      get() {
        return this.$store.state.assessmentRoleDenied.dialogVisible;
      },
      set(visible) {
        if (!visible) {
          this.$store.dispatch('assessmentRoleDenied/closeDialog');
        }
      },
    },
  },
  methods: {
    closeAssessmentRoleDenied() {
      this.$store.dispatch('assessmentRoleDenied/closeDialog');
    },
  },
  mounted() {
    // Ensure lang is a valid locale string
    const validLocale = this.$store.state.auth.lang && ['en', 'de'].includes(this.$store.state.auth.lang)
      ? this.$store.state.auth.lang
      : 'en';
    this.$root.$i18n.locale = validLocale;
    applyPrimeVuePaginatorAria(this.$primevue?.config, validLocale);
  },
  watch: {
    '$store.state.auth.lang': function syncPrimeVuePaginatorAria(lang) {
      const validLocale = lang && ['en', 'de'].includes(lang) ? lang : 'en';
      applyPrimeVuePaginatorAria(this.$primevue?.config, validLocale);
    },
    'toasts.length': function (newLength, oldLength) {
      if (newLength > oldLength) {
        this.toasts.forEach((toast) => {
          this.$toast.add(toast);
        });
        this.$store.commit('notifications/CLEAR_TOASTS');
      }
    },
  },
};
</script>
<style>
::-webkit-scrollbar {
  width: 0.6em;
  height: 0.6em;
  border-radius: 10px;
}

::-webkit-scrollbar-track {
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  border-radius: 10px;
  background-color: #f49718;
}

body {
  overflow-x: hidden;
  font-family: AsenPro, sans-serif;
}

ol li {
  list-style-type: decimal;
  padding-left: 0.25rem;
}

ul li {
  list-style-type: disc;
  padding-left: 0.1rem;
}

.p-inputtext:enabled:focus,
.p-multiselect:not(.p-disabled).p-focus,
.p-checkbox-checked .p-checkbox-box,
.p-select:not(.p-disabled).p-focus,
.p-radiobutton-checked .p-radiobutton-box,
.p-textarea:enabled:focus,
.p-component .p-confirmdialog-accept-button.p-button {
    border-color: var(--color-orange) !important;
}

.p-iftalabel:has(input:focus) label,
.p-select-option.p-select-option-selected,
.p-dialog-close-button:hover,
.p-listbox .p-listbox-list .p-listbox-option.p-listbox-option-selected {
    color: var(--color-orange) !important;
}

.p-checkbox-checked .p-checkbox-box,
.p-radiobutton-checked .p-radiobutton-box,
.p-toggleswitch.p-toggleswitch-checked .p-toggleswitch-slider,
.p-component .p-confirmdialog-accept-button.p-button,
.p-progressbar-value {
    background-color: var(--color-orange) !important;
}

.p-select-option.p-select-option-selected.p-focus,
.p-select-option.p-select-option-selected,
.p-listbox .p-listbox-list .p-listbox-option.p-listbox-option-selected {
    background-color: var(--color-bisque) !important;
}

.p-dialog-close-button {
  position: absolute !important;
  top: 0.5rem !important;
  right: 0.5rem !important;
}

.black-bg {
    background-color: black !important;
    color: white !important;
    border-color: black !important;
}

.notifications.card {
    background-color: black !important;
    border-color: black !important;
    border-radius: 10px !important;
    color: white !important;
    padding: 1rem !important;
}

.p-breadcrumb {
   background: none !important;
}

ol li.p-breadcrumb-item {
    list-style-type: none !important;
}

button {
    @apply flex flex-row gap-x-2 items-center justify-center;
}

.p-menubar-item:not(.p-disabled) > .p-menubar-item-content:hover,
.p-menubar-item.p-focus > .p-menubar-item-content,
.p-menubar-item-active > .p-menubar-item-content,
.p-menubar-submenu,
.p-menubar-root-list {
    background: var(--color-black) !important;
    border-color: var(--color-black) !important;
}

.p-multiselect-label {
    display: flex;
    flex-direction: row !important;
    flex-wrap: wrap !important;
}

.p-menubar-mobile .p-menubar-button .p-icon{
    width: 50px !important;
    height: 50px !important;
}

.p-menubar-button:hover {
    background-color: transparent !important;
}

.p-menu-list .p-menu-submenu-label {
  margin-left: 0.75rem !important;
}
</style>
